import { ErrorMessage } from '@hookform/error-message';
import { useMemo } from 'react';
import { Control, Controller, FieldPathByValue, FieldValues, useController } from 'react-hook-form';

import type { Borrower } from '@willow/graphql-iso/src/app';
import { getBorrowerNameWithoutMiddleName } from '@willow/shared-iso';
import { BorrowerId } from '@willow/types-iso';

import { Form } from '../../../../bootstrap';
import { WillowMultiSelect } from './WillowSelect';

export type BorrowerSelectOption = Pick<
  Borrower,
  'id' | 'firstName' | 'lastName' | 'suffix' | 'email' | 'emailConsent'
>;

type Props<T extends FieldValues> = {
  borrowers: BorrowerSelectOption[];
  fieldName: FieldPathByValue<T, BorrowerId[] | undefined>;
  control: Control<T>;
  allowAllSelections?: boolean; // some components can select even if email / consent is not provided
  hideErrorMessage?: boolean;
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
};

export function ControlledBorrowerMultiSelect<T extends FieldValues>({
  borrowers,
  fieldName,
  control,
  allowAllSelections = true,
  hideErrorMessage = false,
  label,
  placeholder,
  required,
  disabled,
}: Props<T>) {
  const {
    formState: { errors },
  } = useController({ control, name: fieldName });

  const options = useMemo(
    () =>
      borrowers.map((borrower) => {
        const disabledMessage = !borrower.email
          ? 'email not provided'
          : !borrower.emailConsent
          ? 'email consent disabled'
          : undefined;

        return {
          value: borrower.id,
          label: (
            <>
              {getBorrowerNameWithoutMiddleName(borrower)}{' '}
              {disabledMessage && <span className="u-color-red1 fst-italic">({disabledMessage})</span>}
            </>
          ),
          isDisabled: !allowAllSelections && disabledMessage != null,
        };
      }),
    [borrowers, allowAllSelections],
  );

  return (
    <>
      {label && (
        <Form.Label className="u-fs-2 u-bold">
          {label}
          {required && <span className="u-color-red1 ps-1 ">*</span>}
        </Form.Label>
      )}
      <Controller
        name={fieldName}
        control={control}
        render={({ field: { onChange, value } }) => (
          <WillowMultiSelect
            required={required}
            isDisabled={disabled}
            placeholder={placeholder ?? 'Select recipients(s)'}
            options={options}
            value={value ? options.filter((option) => value.includes(option.value)) : []}
            onChange={(options) => {
              onChange(options ? options.map((option) => option.value) : []);
            }}
          />
        )}
      />
      {!hideErrorMessage && (
        <ErrorMessage
          errors={errors}
          name={fieldName as any}
          render={({ message }) => <div className="u-fs-2 u-color-red1">{message}</div>}
        />
      )}
    </>
  );
}
